import { useEffect, useRef, useState } from 'react';

import { FEED_ITEM_EXIT_MS } from 'modules/Tasks/common/FeedItemTransition';

/**
 * Reports whether a feed list is empty, but holds off on flipping to `true` until a just-removed item
 * has had time to finish its FeedItemTransition exit animation — so the empty/celebration UI doesn't
 * replace the list mid-animation and cut the last item's collapse short. Becoming non-empty is
 * reported immediately.
 *
 * A change in `resetKey` (e.g. the active filter/surface) syncs immediately with no delay: those
 * paths remount the animated list, so there's no exit animation to wait for.
 */
export default function useSettledEmpty(isEmpty: boolean, resetKey?: unknown): boolean {
    const [settled, setSettled] = useState(isEmpty);
    const resetKeyRef = useRef(resetKey);

    useEffect(() => {
        if (resetKeyRef.current !== resetKey) {
            resetKeyRef.current = resetKey;
            setSettled(isEmpty);
            return undefined;
        }
        if (!isEmpty) {
            setSettled(false);
            return undefined;
        }
        const timer = setTimeout(() => setSettled(true), FEED_ITEM_EXIT_MS);
        return () => clearTimeout(timer);
    }, [isEmpty, resetKey]);

    // Gate on the live `isEmpty` so becoming non-empty is reported this render, without waiting for the
    // effect to clear `settled`. That one-render lag would otherwise mount the list a render late — which
    // breaks consumers that measure the DOM on mount (e.g. the carousel's pagination sizing).
    return isEmpty && settled;
}
